import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import {
  ConversionFilterOption,
  conversionDefaultOptions,
} from '@bingads-webui-campaign/overview-cards-common';
import { InstrumentationLog } from '../common/instrumentation';
import EmptyState from './empty-state';
import ConversionTrackingState from './conversion-tracking-state';
import { StringsMapping } from '../common/strings';

class ConversionTrackingCard extends React.Component {
  constructor(props) {
    super(props);
    const {
      metric,
      selectedMenuItem,
      dateRange,
      fetch, // fetch for conversion tracking data
      fetchSingleTile,

      i18n,
      newI18n,
      deps,
      entitySet,
      permissions,

      triggerChangeMenuItem,
      triggerChangeMetric,
      TrackingTypeFilterStore,
      GoalCategoryFilterStore,

      blockCard,
      willFetchData,
      renderedWithData,
      unblockCard,
    } = props;

    this.fetch = fetch;
    this.state = {
      dataLoaded: false,
      dateRange,
      metric: metric || conversionDefaultOptions.metric, // grid data type
      selectedMenuItem: selectedMenuItem || conversionDefaultOptions.selectedMenuItem, // tile dropdown
      data: {},
    };

    this.i18n = i18n;
    this.newI18n = newI18n;
    this.deps = deps;
    this.instrumentationLog = InstrumentationLog({
      instrumentation: deps.instrumentation,
      constants: deps.constants,
    });

    this.permissions = permissions;
    this.entitySet = entitySet;
    this.triggerChangeMenuItem = triggerChangeMenuItem;
    this.triggerChangeMetric = triggerChangeMetric;

    this.defaultMenuItems = [{
      displayText: this.newI18n.getString(StringsMapping.All_Change),
      action: ConversionFilterOption.AllChanges,
    }, {
      displayText: this.newI18n.getString(StringsMapping.Top_Gainers),
      action: ConversionFilterOption.TopGainers,
    }, {
      displayText: this.newI18n.getString(StringsMapping.Top_Decliners),
      action: ConversionFilterOption.TopDecliners,
    }];

    this.currentMenuItems = [];

    this.blockCard = blockCard;
    this.willFetchData = willFetchData;
    this.renderedWithData = renderedWithData;
    this.unblockCard = unblockCard;
    this.TrackingTypeFilterStore = TrackingTypeFilterStore;
    this.GoalCategoryFilterStore = GoalCategoryFilterStore;
    this.fetchSingleTile = fetchSingleTile;
  }

  componentDidMount() {
    this.fetchTileData(this.fetchSingleTile);
  }


  componentDidUpdate(prevProps, prevState) {
    if (this.isDataFetchRequired(prevState)) {
      const fetchSingleTile = this.isFetchSingleTile(prevState);
      this.fetchTileData(fetchSingleTile);
    }
  }

  getMenuItems = () => this.currentMenuItems;

  isDataFetchRequired = (prevState) => {
    const {
      dateRange: prevDateRange,
      selectedMenuItem: prevMenuItem,
      metric: prevMetric,
    } = prevState;

    const {
      dateRange,
      selectedMenuItem,
      metric,
    } = this.state;

    if (metric === prevMetric && selectedMenuItem === prevMenuItem && _.isEqual(dateRange, prevDateRange)) {
      return false;
    }
    return true;
  };

  isFetchSingleTile = (prevState) => {
    const {
      dateRange: prevDateRange,
    } = prevState;

    const {
      dateRange,
    } = this.state;

    if (!_.isEqual(dateRange, prevDateRange)) {
      return false;
    }
    return true;
  };

  updateCurrentMenuItems = (isMenuItemNeeded) => {
    if (isMenuItemNeeded) {
      const { selectedMenuItem } = this.state;
      this.currentMenuItems = this.defaultMenuItems;
      _.each(this.currentMenuItems, (item) => {
        if (item.action === selectedMenuItem) {
        // eslint-disable-next-line no-param-reassign
          item.preset = true;
        } else {
        // eslint-disable-next-line no-param-reassign
          item.preset = false;
        }
      });
      this.triggerChangeMenuItem(this.currentMenuItems);
    } else {
      this.currentMenuItems = [];
      this.triggerChangeMenuItem(this.currentMenuItems);
    }
  };

  fetchTileData = (fetchSingleTile) => {
    const {
      dateRange,
      selectedMenuItem,
      metric,
    } = this.state;

    if (!dateRange) { return; }
    if (!this.fetch) { return; }

    this.blockCard();
    this.willFetchData();

    this.fetch({
      dateRange,
      metric,
      selectedMenuItem,
      fetchSingleTile,
    }).then((data) => {
      if (data) {
        this.setState({
          dataLoaded: true,
          data,
        });
      }
    }).finally(() => {
      this.renderedWithData();
      this.unblockCard();
    });
  };

  handleUpdate = ({ dateRange, metric, selectedMenuItem }) => {
    if (dateRange) {
      this.setState({ dateRange });
    } else if (metric) {
      this.setState({ metric });
    } else if (selectedMenuItem) {
      this.setState({ selectedMenuItem });
      this.updateCurrentMenuItems(true);
    }
  };

  render() {
    const { dataLoaded } = this.state;
    if (!dataLoaded) {
      return (<div className="conversion-tracking-card-data-loading" />);
    }

    const {
      data: {
        ConversionGoalCategoryResults,
        ConversionGoalTrackingStatusSummaries,
        NoConversionGoalsExist,
        NoConversionGoalsWithRevenue,
      },
    } = this.state;

    const {
      newI18n,
      i18n,
      permissions,
      deps,
      instrumentationLog,
    } = this;

    if (NoConversionGoalsExist) {
      this.updateCurrentMenuItems(false);

      return (
        <EmptyState
          i18n={i18n}
          newI18n={newI18n}
          router={deps.legacyRouter}
          pageContext={deps.pageContext}
          instrumentationLog={instrumentationLog}
          permissions={permissions}
          blockCard={this.blockCard}
          willFetchData={this.willFetchData}
          renderedWithData={this.renderedWithData}
          unblockCard={this.unblockCard}
        />
      );
    }

    const {
      dateRange,
      metric,
      selectedMenuItem,
    } = this.state;

    const {
      entitySet,
      updateCurrentMenuItems,
      TrackingTypeFilterStore,
      GoalCategoryFilterStore,
    } = this;

    return (
      <ConversionTrackingState
        dateRange={dateRange}
        categoryResults={ConversionGoalCategoryResults}
        statusSummaries={ConversionGoalTrackingStatusSummaries}
        noConversionGoalsWithRevenue={NoConversionGoalsWithRevenue}
        metric={metric}
        selectedMenuItem={selectedMenuItem}
        updateCurrentMenuItems={updateCurrentMenuItems}
        updateMetric={(newMetric) => { this.handleUpdate({ metric: newMetric }); }}

        deps={deps}
        entitySet={entitySet}
        i18n={i18n}
        newI18n={newI18n}
        permissions={permissions}
        instrumentationLog={instrumentationLog}
        TrackingTypeFilterStore={TrackingTypeFilterStore}
        GoalCategoryFilterStore={GoalCategoryFilterStore}
      />
    );
  }
}

ConversionTrackingCard.defaultProps = {
  metric: conversionDefaultOptions.metric,
  selectedMenuItem: conversionDefaultOptions.selectedMenuItem,
  dateRange: {},
  triggerChangeMenuItem: _.noop,
  blockCard: _.noop,
  willFetchData: _.noop,
  renderedWithData: _.noop,
  unblockCard: _.noop,
  triggerChangeMetric: _.noop,
  fetch: _.noop,
  TrackingTypeFilterStore: _.noop,
  GoalCategoryFilterStore: _.noop,
  fetchSingleTile: false,
};

ConversionTrackingCard.propTypes = {
  metric: PropTypes.string,
  selectedMenuItem: PropTypes.string,
  fetchSingleTile: PropTypes.bool,
  dateRange: PropTypes.shape({
    RangeStartDate: PropTypes.string,
    RangeEndDate: PropTypes.string,
    RangeId: PropTypes.number,
    Rangelcid: PropTypes.number,
    compared: PropTypes.bool,
    start: PropTypes.objectOf(Date),
    end: PropTypes.objectOf(Date),
    grain: PropTypes.number,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  deps: PropTypes.shape({
    constants: PropTypes.shape({}).isRequired,
    instrumentation: PropTypes.shape({}).isRequired,
  }).isRequired,
  entitySet: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  triggerChangeMenuItem: PropTypes.func,
  blockCard: PropTypes.func,
  willFetchData: PropTypes.func,
  renderedWithData: PropTypes.func,
  unblockCard: PropTypes.func,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  triggerChangeMetric: PropTypes.func,
  fetch: PropTypes.func,
  TrackingTypeFilterStore: PropTypes.func,
  GoalCategoryFilterStore: PropTypes.func,
};

export default ConversionTrackingCard;
